import { CreateEventFormData, CreateEventFormErrors, PublishType } from '@/types/admin/createEvent';
import { IEventPhoto } from '@/types/admin/events';
import { useState, useCallback } from 'react';
import { composeValidators, requiredString, dateOrder, positiveIntegerOrZero, requireIf, validateData } from '@/utils/admin/validation';
import { showConfirm } from '@/utils/common/alert';

// Helper function to get next full hour (matching admin-web behavior)
const getNextFullHour = (hoursToAdd: number = 1): Date => {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + hoursToAdd, 0, 0, 0);
  return nextHour;
};

const getDefaultFormData = (): CreateEventFormData => ({
  name: '',
  description: '',
  location: '',
  startTime: getNextFullHour(1),
  endTime: getNextFullHour(2),
  rsvpLimit: '0',
  internalNotes: '',
  publishType: 'publishNow',
  photos: [],
});

export const useCreateEventForm = () => {
  const [formData, setFormData] = useState<CreateEventFormData>(getDefaultFormData);
  const [errors, setErrors] = useState<CreateEventFormErrors>({});

  const validateForm = useCallback((): boolean => {
    const validator = composeValidators<CreateEventFormData>(
      requiredString('name', 'Event name'),
      requiredString('description', 'Event description'),
      requiredString('location', 'Event location'),
      dateOrder('startTime', 'endTime', 'End time must be after start time'),
      positiveIntegerOrZero('rsvpLimit', 'RSVP limit'),
      requireIf((d) => d.publishType === 'scheduled', 'publishTime', 'Publish time'),
    );
    return validateData(formData, setErrors as any, validator);
  }, [formData]);

  const handleFieldChange = useCallback((field: keyof CreateEventFormData, value: string | Date | IEventPhoto[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing/changing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handlePublishTypeChange = useCallback((type: PublishType) => {
    setFormData(prev => ({
      ...prev,
      publishType: type,
      publishTime: type === 'publishNow' ? new Date() : type === 'scheduled' ? new Date() : undefined,
    }));
  }, []);

  const appendPhotos = useCallback((photosToAdd: IEventPhoto[]) => {
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, ...photosToAdd],
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(getDefaultFormData());
    setErrors({});
  }, []);

  const showDiscardAlert = useCallback(async (onDiscard: () => void) => {
    const confirmed = await showConfirm(
      'Discard Changes',
      'Are you sure you want to discard your changes?',
      'Discard',
      'Keep Editing'
    );
    
    if (confirmed) {
      onDiscard();
    }
  }, []);

  return {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    appendPhotos,
    resetForm,
    showDiscardAlert,
  };
};
